import clsx from 'clsx';
import type {
  MetricHistoryEntry,
  OutputThresholds,
} from '@arcanum-hq/shared-types';
import { Chip, type ChipVariant } from '@/components/ui/Chip';
import { Sparkline } from '@/components/ui/Sparkline';
import { formatCurrency, formatMultiple, formatPercent } from '@/lib/format';
import { ThresholdBand, type ThresholdFormat } from './ThresholdBand';

/**
 * KPI allowlist — only these 4 prominent metrics carry a sparkline so
 * panel density doesn't dilute. Phase 6's Hero Metrics row reuses the same
 * primitive (and ideally the same allowlist).
 */
export const SPARKLINE_KPI_ALLOWLIST: ReadonlySet<string> = new Set([
  'IRR',
  'EquityMultiple',
  'StabilizedDSCR',
  'StabilizedCoC',
]);

/**
 * Direction-of-improvement for the KPI allowlist. All four currently are
 * "higher is better" → 'up'. If a future KPI flips (e.g. a cap-rate exit
 * that's "lower is better"), add it here with `'down'`.
 */
const KPI_POSITIVE_TREND: Record<string, 'up' | 'down'> = {
  IRR: 'up',
  EquityMultiple: 'up',
  StabilizedDSCR: 'up',
  StabilizedCoC: 'up',
};

const MULTIPLE_OVERRIDE_NAMES: ReadonlySet<string> = new Set([
  'EquityMultiple',
  'GPEquityMultiple',
  'LPEquityMultiple',
]);

export interface PerformanceTileProps {
  /** Internal name (e.g. `IRR`). Used for stable keys + data attributes. */
  name: string;
  /** Display label shown above the value (e.g. `IRR`, `Equity Multiple`). */
  label: string;
  /** Current engine value. `null` when the model can't compute it yet. */
  value: number | null;
  /** Output type — drives value formatting AND delta unit. */
  format: ThresholdFormat;
  /**
   * Optional thresholds. When present, renders the 5-segment band beneath
   * the value. When absent, the band is omitted entirely.
   */
  thresholds?: OutputThresholds;
  /**
   * Per-metric ring buffer (Phase 1 `recordEdit()` writeback). Delta is
   * derived from the most-recent prior entry vs `value`. When the history
   * has fewer than 1 prior entry OR `value` is null, no delta is shown.
   */
  history?: MetricHistoryEntry[];
  /**
   * Single-letter source pill rendered next to the label. Defaults to
   * `agent` (computed output) — Capital Stack rows can override to
   * `user`/`market`/etc. via the parent.
   */
  chipVariant?: ChipVariant;
  /** Pill caption — defaults to the variant's natural label (e.g. "Agent"). */
  chipLabel?: string;
  className?: string;
}

const VARIANT_DEFAULT_LABEL: Record<ChipVariant, string> = {
  user: 'User',
  market: 'Market',
  system: 'System',
  agent: 'Agent',
  invalid: '—',
};

function formatValue(v: number, format: ThresholdFormat): string {
  if (format === 'percentage') return formatPercent(v, 1);
  if (format === 'currency') return formatCurrency(v, 0);
  return formatMultiple(v);
}

interface DeltaRender {
  arrow: '▲' | '▼' | '─';
  text: string;
  raw: number;
}

function buildDelta(
  current: number,
  prior: number,
  format: ThresholdFormat,
): DeltaRender {
  const raw = current - prior;
  const abs = Math.abs(raw);
  const arrow: DeltaRender['arrow'] =
    raw > 0 ? '▲' : raw < 0 ? '▼' : '─';
  const sign = raw > 0 ? '+' : raw < 0 ? '-' : '';

  let text: string;
  if (format === 'percentage') {
    // Show change in percentage points (decimal delta * 100).
    text = `${sign}${(abs * 100).toFixed(1)}pp`;
  } else if (format === 'currency') {
    text = `${sign}${formatCurrency(abs, 0)}`;
  } else {
    text = `${sign}${abs.toFixed(2)}x`;
  }

  return { arrow, text, raw };
}

/**
 * Performance-grid tile composing source pill + value + delta + threshold
 * band. Replaces the prior status-color-only rendering in
 * `<ModelOverviewPanel>` Performance section. Sparkline (Session 2) mounts
 * below the band via the same component.
 *
 * Rendering contract:
 * - Header: source `<Chip>` (small, decorative) + label
 * - Body: large mono value + inline delta (arrow + signed magnitude)
 * - Footer: optional `<ThresholdBand>` when thresholds are configured
 *
 * Null value → renders `—` placeholder + skips delta + skips band.
 * Single-entry history → no prior reference, no delta shown.
 */
export function PerformanceTile({
  name,
  label,
  value,
  format,
  thresholds,
  history,
  chipVariant = 'agent',
  chipLabel,
  className,
}: PerformanceTileProps) {
  const resolvedChipLabel = chipLabel ?? VARIANT_DEFAULT_LABEL[chipVariant];
  const hasValue = value != null && Number.isFinite(value);
  const effectiveFormat: ThresholdFormat = MULTIPLE_OVERRIDE_NAMES.has(name) ? 'multiplier' : format;

  const delta = (() => {
    if (!hasValue || !history || history.length < 1) return null;
    const prior = history[history.length - 1];
    if (!prior || !Number.isFinite(prior.value)) return null;
    if (prior.value === value) return null;
    return buildDelta(value as number, prior.value, effectiveFormat);
  })();

  return (
    <div
      data-testid="performance-tile"
      data-metric={name}
      className={clsx('flex flex-col gap-1', className)}
    >
      <div className="flex items-center gap-1.5">
        <Chip variant={chipVariant} label={resolvedChipLabel} />
        <span className="text-micro font-mono text-text-dim">{label}</span>
      </div>
      <div className="flex items-baseline gap-2">
        <span
          data-testid="performance-tile-value"
          className="text-lg font-mono font-bold tabular-nums text-text-bright"
        >
          {hasValue ? formatValue(value as number, effectiveFormat) : '—'}
        </span>
        {delta && (
          <span
            data-testid="performance-tile-delta"
            data-delta-direction={
              delta.raw > 0 ? 'up' : delta.raw < 0 ? 'down' : 'flat'
            }
            className="text-micro font-mono tabular-nums text-text-muted"
          >
            <span aria-hidden="true">{delta.arrow}</span>{' '}
            <span>{delta.text}</span>
          </span>
        )}
      </div>
      {hasValue && thresholds && (
        <ThresholdBand
          value={value as number}
          thresholds={thresholds}
          format={effectiveFormat}
        />
      )}
      {SPARKLINE_KPI_ALLOWLIST.has(name) && (
        <Sparkline
          values={(history ?? []).map((entry) => entry.value)}
          positiveTrend={KPI_POSITIVE_TREND[name]}
        />
      )}
    </div>
  );
}
